import { useEffect, useState } from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, BellOff, FileSpreadsheet, Layers } from 'lucide-react';
import { api, API_BASE, getToken, fmt } from '../api';
import { SkeletonTable } from '../components/Skeleton';

type Row = {
  ord_num: string;
  mcht_id: string;
  mid: string | null;
  mcht_name: string | null;
  trx_id: string | null;
  appr_num: string | null;
  trx_at: string;
  cxl_at: string | null;
  is_cancel: number;
  is_test: number;
  txn_type: 'discount' | 'realtime';
  settlmnt_cycle: string | null;
  noti_kind: 'merchant' | 'merchant_discount' | null;
  gross_amount: number;
  fee_amount: number;
  net_amount: number;
  fee_rate: number;
  raw_payload: string | null;
  created_at: string;
  updated_at: string;
  settle_gross_sales: number | null;
  settle_exists: number | null;
};

const TYPE_KO: Record<string, string> = { discount: '할인매매', realtime: '실시간' };
const NOTI_KIND_KO: Record<string, string> = {
  merchant: '일반 결제 수신',
  merchant_discount: '할인매매 결제 수신',
};

const SHOW_REALTIME = true;
const NOTI_KIND_CLASS: Record<string, string> = {
  merchant: 'merchant',
  merchant_discount: 'merchant-discount',
};

function todayKstStr(): string {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 3600 * 1000);
  return kst.toISOString().slice(0, 10);
}
export default function Notifications() {
  // URL 쿼리 ?mcht_id=xxx 로 진입 시 해당 가맹점 필터로 자동 설정 (결제가맹점 추가 화면에서 결제내역 보기 링크로 사용).
  const initialMchtId = (typeof window !== 'undefined')
    ? (new URLSearchParams(window.location.search).get('mcht_id') ?? '')
    : '';
  // 기본은 당일 (KST). 운영자가 매일 첫 진입 시 오늘 거 바로 보이도록.
  // mcht_id 진입 시 최근 60일로 확대해서 결제 이력이 오래된 경우에도 보이도록.
  const initialFrom = initialMchtId
    ? (() => { const d = new Date(Date.now() + 9 * 3600 * 1000); d.setUTCDate(d.getUTCDate() - 60); return d.toISOString().slice(0, 10); })()
    : todayKstStr();
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(todayKstStr());
  const [mchtId, setMchtId] = useState(initialMchtId);
  const [isCancel, setIsCancel] = useState<'' | '0' | '1'>('');
  const [txnType, setTxnType] = useState<'' | 'discount' | 'realtime'>('');
  const [notiKind, setNotiKind] = useState<'' | 'merchant' | 'merchant_discount'>('');
  const [settlmntCycle, setSettlmntCycle] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [kw, setKw] = useState('');
  const [ordNum, setOrdNum] = useState('');
  const [mid, setMid] = useState('');
  const [tid, setTid] = useState('');
  const [trxId, setTrxId] = useState('');
  const [apprNum, setApprNum] = useState('');
  const [mchtName, setMchtName] = useState('');
  const [cardNum, setCardNum] = useState('');
  const [issuer, setIssuer] = useState('');
  const [method, setMethod] = useState('');
  const [installment, setInstallment] = useState('');
  const [itemName, setItemName] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [feeRateMin, setFeeRateMin] = useState('');
  const [feeRateMax, setFeeRateMax] = useState('');
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [detail, setDetail] = useState<Row | null>(null);

  useEffect(() => {
    let active = true;
    setErr(null); setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (mchtId.trim()) params.set('mcht_id', mchtId.trim());
    if (isCancel) params.set('is_cancel', isCancel);
    if (txnType) params.set('txn_type', txnType);
    if (notiKind) params.set('noti_kind', notiKind);
    if (settlmntCycle.trim()) params.set('settlmnt_cycle', settlmntCycle.trim());
    if (!includeTest) params.set('exclude_test', '1');
    if (kw.trim()) params.set('q', kw.trim());
    if (ordNum.trim()) params.set('ord_num', ordNum.trim());
    if (mid.trim()) params.set('mid', mid.trim());
    if (tid.trim()) params.set('tid', tid.trim());
    if (trxId.trim()) params.set('trx_id', trxId.trim());
    if (apprNum.trim()) params.set('appr_num', apprNum.trim());
    if (mchtName.trim()) params.set('mcht_name', mchtName.trim());
    if (cardNum.trim()) params.set('card_num', cardNum.trim());
    if (issuer.trim()) params.set('issuer', issuer.trim());
    if (method.trim()) params.set('method', method.trim());
    if (installment.trim()) params.set('installment', installment.trim());
    if (itemName.trim()) params.set('item_name', itemName.trim());
    if (amountMin.trim()) params.set('amount_min', amountMin.trim());
    if (amountMax.trim()) params.set('amount_max', amountMax.trim());
    if (feeRateMin.trim()) params.set('fee_rate_min', feeRateMin.trim());
    if (feeRateMax.trim()) params.set('fee_rate_max', feeRateMax.trim());
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    (async () => {
      try {
        const r = await api<{ total: number; data: Row[] }>('GET', `/notifications/list?${params}`);
        if (!active) return;
        setData(r.data ?? []);
        setTotal(r.total ?? 0);
      } catch (e: any) {
        if (active) setErr(e?.message ?? '조회 실패');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [
    from, to, mchtId, isCancel, txnType, notiKind, settlmntCycle, includeTest,
    kw, ordNum, mid, tid, trxId, apprNum, mchtName, cardNum, issuer, method, installment, itemName,
    amountMin, amountMax, feeRateMin, feeRateMax,
    limit, offset,
  ]);

  const onApply = (e: React.FormEvent) => { e.preventDefault(); setOffset(0); };
  const reflectedLabel = (r: Row): { text: string; color: string } => {
    if (!r.settle_exists) return { text: '정산 내역 미생성', color: 'var(--muted)' };
    if (r.txn_type === 'realtime') return { text: '해당없음', color: 'var(--muted)' };
    if ((r.settle_gross_sales ?? 0) === 0 && r.gross_amount !== 0) return { text: '정산 반영 확인 필요', color: 'var(--warn)' };
    return { text: '반영됨', color: 'var(--good)' };
  };
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const curPage = Math.floor(offset / limit) + 1;
  const firstIdx = total === 0 ? 0 : offset + 1;
  const lastIdx = Math.min(offset + limit, total);
  const goPage = (p: number) => {
    const safe = Math.max(1, Math.min(totalPages, p));
    setOffset((safe - 1) * limit);
  };

  const resetAdvancedFilters = () => {
    setKw(''); setOrdNum(''); setMid(''); setTid(''); setTrxId(''); setApprNum('');
    setMchtName(''); setCardNum(''); setIssuer(''); setMethod(''); setInstallment(''); setItemName('');
    setAmountMin(''); setAmountMax(''); setFeeRateMin(''); setFeeRateMax('');
    setOffset(0);
  };

  // 현재 필터 → URLSearchParams (export·재사용용)
  const buildQueryParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (mchtId.trim()) params.set('mcht_id', mchtId.trim());
    if (isCancel) params.set('is_cancel', isCancel);
    if (txnType) params.set('txn_type', txnType);
    if (notiKind) params.set('noti_kind', notiKind);
    if (settlmntCycle.trim()) params.set('settlmnt_cycle', settlmntCycle.trim());
    if (!includeTest) params.set('exclude_test', '1');
    if (kw.trim()) params.set('q', kw.trim());
    if (ordNum.trim()) params.set('ord_num', ordNum.trim());
    if (mid.trim()) params.set('mid', mid.trim());
    if (tid.trim()) params.set('tid', tid.trim());
    if (trxId.trim()) params.set('trx_id', trxId.trim());
    if (apprNum.trim()) params.set('appr_num', apprNum.trim());
    if (mchtName.trim()) params.set('mcht_name', mchtName.trim());
    if (cardNum.trim()) params.set('card_num', cardNum.trim());
    if (issuer.trim()) params.set('issuer', issuer.trim());
    if (method.trim()) params.set('method', method.trim());
    if (installment.trim()) params.set('installment', installment.trim());
    if (itemName.trim()) params.set('item_name', itemName.trim());
    if (amountMin.trim()) params.set('amount_min', amountMin.trim());
    if (amountMax.trim()) params.set('amount_max', amountMax.trim());
    if (feeRateMin.trim()) params.set('fee_rate_min', feeRateMin.trim());
    if (feeRateMax.trim()) params.set('fee_rate_max', feeRateMax.trim());
    return params;
  };

  // 다운로드 — CSV(UTF-8 BOM, Excel 호환) 또는 XLSX(식별자 컬럼 텍스트 형식 보존).
  // 현재 필터 그대로 매칭 전체 행 export. 서버 상한 50,000행.
  const [exporting, setExporting] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const download = async (fmt: 'csv' | 'xlsx') => {
    setDownloadOpen(false);
    setExporting(true);
    try {
      const params = buildQueryParams();
      const tok = getToken();
      const path = fmt === 'xlsx' ? '/notifications/export.xlsx' : '/notifications/export.csv';
      const res = await fetch(`${API_BASE}${path}?${params}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cat = txnType ? `_${txnType === 'discount' ? '할인매매' : '실시간'}` : '';
      const tag = (from === to) ? from : `${from}_${to}`;
      a.download = `서포트리_결제수신내역${cat}_${tag}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      alert(`다운로드 실패: ${e?.message ?? '오류'}`);
    } finally { setExporting(false); }
  };

  return (
    <div>
      <header className="page-header">
        <div>
          <h2 className="page-title">결제 수신내역</h2>
          <p className="page-sub">PG 결제 수신내역이 도착한 거래 원장입니다. 할인매매 거래는 도착 즉시 해당 가맹점의 정산 반영 내역에 누적됩니다.</p>
        </div>
        <div className="page-actions" style={{ position: 'relative' }}>
          <button onClick={() => setDownloadOpen(o => !o)} disabled={exporting} className="filter-warn-btn"
                  title="현재 필터·날짜 그대로 다운로드. Excel(.xlsx)은 MID·주문번호·카드번호 등 식별자의 자동 숫자 변환/앞자리 0 삭제를 방지.">
            <FileSpreadsheet size={14} />
            {exporting ? '내보내는 중…' : '다운로드'}
            <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.85 }}>▾</span>
          </button>
          {downloadOpen && !exporting && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 4,
              background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 40, minWidth: 260,
            }}>
              <button onClick={() => download('xlsx')} className="filter-ghost"
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 0, borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Excel (.xlsx) 다운로드</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  MID·주문번호·승인번호·카드번호·TID 텍스트 형식 유지 (권장)
                </div>
              </button>
              <button onClick={() => download('csv')} className="filter-ghost"
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>CSV (.csv) 다운로드</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  UTF-8 BOM · Excel/AI 호환. 열 서식은 별도 지정되지 않음.
                </div>
              </button>
            </div>
          )}
        </div>
      </header>

      <details className="card help-block">
        <summary>ⓘ 결제 수신내역 화면 안내 — 용어 설명</summary>
        <div className="help-body">
          <h4>이 화면이 보여주는 것</h4>
          <ul>
            <li>PG(한국결제데이터)가 우리 서버로 보낸 결제 수신내역 중 <b>저장 성공한 거래</b>의 원장.</li>
            <li>저장 단위는 <b>주문번호</b> 단위. 같은 주문번호로 결제 → 취소가 오면 동일 행이 취소 상태로 갱신됨.</li>
            <li>기본 필터는 운영 거래(테스트 제외). "테스트 포함" 체크 시 테스트 발신도 노란 강조로 노출.</li>
          </ul>

          <h4>주요 컬럼 의미</h4>
          <ul>
            <li><b>결제 발생시각</b>: PG가 보낸 거래 발생시각 (KST).</li>
            <li><b>수신시각</b>: 우리 서버가 결제 수신 요청을 받은 시각 (KST).</li>
            <li><b>결제 수신 유형</b>: 일반 결제 수신 / 할인매매 결제 수신.</li>
            <li><b>유형</b>: 할인매매 = 다음날 입금/일차감 정산 대상. 실시간 = 별도 흐름.</li>
            <li><b>취소</b>: 취소로 통지된 거래. 매출액이 음수로 저장됨.</li>
            <li><b>매출액 / 수수료 / 순액</b>: 결제금액 / 수수료 / 가맹점 지급 기준 (= 매출액 − 수수료).</li>
            <li><b>정산반영</b>:
              <ul>
                <li>"반영됨" = 해당 일자 정산 반영 내역에 매출이 잡혀 있음 (녹색).</li>
                <li>"정산 반영 확인 필요" = 정산 반영 내역은 있는데 매출 합이 0 (빨강) — 점검 필요.</li>
                <li>"정산 내역 미생성" = 정산 반영 내역 자체가 아직 없음. 정산 화면을 한 번 열거나 [누락 정산 일괄 반영]을 누르면 생성.</li>
                <li>"해당없음" = 실시간 거래는 별도 흐름이라 정산 반영 평가에서 제외.</li>
              </ul>
            </li>
            <li><b>주문번호</b>: PG가 부여한 거래 고유번호. <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>기술 상세: ord_num (PK)</span></li>
          </ul>

          <h4>관련 화면</h4>
          <ul>
            <li><b>결제 수신 로그</b>: 저장 실패 포함 모든 결제 수신 요청의 수신 원문·처리 결과.</li>
            <li><b>결제가맹점 추가</b>: 결제 수신내역은 있는데 가맹점 정보가 부족한 가맹점 목록.</li>
            <li><b>일일 정산</b>: 결제 수신 매출을 기반으로 산정된 정산 결과.</li>
          </ul>
        </div>
      </details>

      <div className="card filter-card">
        <form onSubmit={onApply} className="filter-form">
          <label className="filter-field">시작일
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setOffset(0); }} />
          </label>
          <label className="filter-field">종료일
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setOffset(0); }} />
          </label>
          <label className="filter-field">MID / 가맹점 ID
            <input type="text" value={mchtId} placeholder="예: gpay7475dm"
                   onChange={e => setMchtId(e.target.value)} style={{ minWidth: 180 }} />
          </label>
          <label className="filter-field">취소여부
            <select value={isCancel} onChange={e => { setIsCancel(e.target.value as any); setOffset(0); }}>
              <option value="">전체</option>
              <option value="0">정상</option>
              <option value="1">취소</option>
            </select>
          </label>
          {SHOW_REALTIME && (
            <label className="filter-field" title="기술 필드명: noti_kind">결제 수신 유형
              <select value={notiKind} onChange={e => { setNotiKind(e.target.value as any); setOffset(0); }}>
                <option value="">전체</option>
                <option value="merchant">일반 결제 수신</option>
                <option value="merchant_discount">할인매매 결제 수신</option>
              </select>
            </label>
          )}
          <label className="filter-field" title="개발자 필드명: settlmnt_cycle">정산주기
            <input type="text" value={settlmntCycle} onChange={e => { setSettlmntCycle(e.target.value); setOffset(0); }}
                   placeholder="D1/W1/M1" style={{ width: 120 }} />
          </label>
          {SHOW_REALTIME && (
            <label className="filter-field" title="개발자 필드명: txn_type">거래유형
              <select value={txnType} onChange={e => { setTxnType(e.target.value as any); setOffset(0); }}>
                <option value="">전체</option>
                <option value="discount">할인매매</option>
                <option value="realtime">실시간</option>
              </select>
            </label>
          )}
          <label className="filter-field">통합검색 (키워드)
            <input type="text" value={kw} placeholder="가맹점명·주문번호·승인번호·카드번호 등"
                   onChange={e => { setKw(e.target.value); setOffset(0); }} style={{ minWidth: 280 }} />
          </label>
          <label className="check">
            <input type="checkbox" checked={includeTest} onChange={e => { setIncludeTest(e.target.checked); setOffset(0); }} />
            테스트 포함
          </label>
          <button type="submit" className="filter-primary">조회</button>
        </form>

        <details className="more-filters">
          <summary>상세 필터</summary>
          <div className="body">
            <label className="filter-field">주문번호(ord_num)
              <input type="text" value={ordNum} onChange={e => { setOrdNum(e.target.value); setOffset(0); }} style={{ minWidth: 200 }} placeholder="정확히 일치" />
            </label>
            <label className="filter-field">PG MID
              <input type="text" value={mid} onChange={e => { setMid(e.target.value); setOffset(0); }} placeholder="정확히 일치" />
            </label>
            <label className="filter-field">단말기 번호(TID)
              <input type="text" value={tid} onChange={e => { setTid(e.target.value); setOffset(0); }} placeholder="정확히 일치" />
            </label>
            <label className="filter-field">거래 ID(trx_id)
              <input type="text" value={trxId} onChange={e => { setTrxId(e.target.value); setOffset(0); }} placeholder="정확히 일치" />
            </label>
            <label className="filter-field">승인번호(appr_num)
              <input type="text" value={apprNum} onChange={e => { setApprNum(e.target.value); setOffset(0); }} placeholder="정확히 일치" />
            </label>
            <label className="filter-field">가맹점명
              <input type="text" value={mchtName} onChange={e => { setMchtName(e.target.value); setOffset(0); }} placeholder="부분 일치" />
            </label>
            <label className="filter-field">카드번호
              <input type="text" value={cardNum} onChange={e => { setCardNum(e.target.value); setOffset(0); }} placeholder="부분 일치" />
            </label>
            <label className="filter-field">발급사(issuer)
              <input type="text" value={issuer} onChange={e => { setIssuer(e.target.value); setOffset(0); }} placeholder="정확히 일치" />
            </label>
            <label className="filter-field">결제수단(method)
              <input type="text" value={method} onChange={e => { setMethod(e.target.value); setOffset(0); }} placeholder="단말기/수기 등" />
            </label>
            <label className="filter-field">할부(installment)
              <input type="text" value={installment} onChange={e => { setInstallment(e.target.value); setOffset(0); }} placeholder="예: 00 / 03" />
            </label>
            <label className="filter-field">상품명(item_name)
              <input type="text" value={itemName} onChange={e => { setItemName(e.target.value); setOffset(0); }} placeholder="부분 일치" />
            </label>
            <label className="filter-field">매출액 ≥
              <input type="number" value={amountMin} onChange={e => { setAmountMin(e.target.value); setOffset(0); }} placeholder="원" style={{ width: 120 }} />
            </label>
            <label className="filter-field">매출액 ≤
              <input type="number" value={amountMax} onChange={e => { setAmountMax(e.target.value); setOffset(0); }} placeholder="원" style={{ width: 120 }} />
            </label>
            <label className="filter-field">수수료율 ≥
              <input type="number" step="0.0001" value={feeRateMin} onChange={e => { setFeeRateMin(e.target.value); setOffset(0); }} placeholder="0.044" style={{ width: 100 }} />
            </label>
            <label className="filter-field">수수료율 ≤
              <input type="number" step="0.0001" value={feeRateMax} onChange={e => { setFeeRateMax(e.target.value); setOffset(0); }} placeholder="0.044" style={{ width: 100 }} />
            </label>
            <button type="button" onClick={resetAdvancedFilters} className="filter-ghost">상세 필터 초기화</button>
          </div>
        </details>

        {err && <div className="error">{err}</div>}
      </div>

      {/* 거래유형 카테고리 탭 — SHOW_REALTIME=false 시 전체 숨김 (할인매매 단일 운영). */}
      {SHOW_REALTIME && (
        <div className="card" style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Layers size={13} /> 거래유형
          </span>
          {[
            { v: '' as const,         label: '전체',     accent: 'var(--text)' },
            { v: 'discount' as const, label: '할인매매', accent: 'var(--brand-700)' },
            { v: 'realtime' as const, label: '실시간',   accent: '#2563eb' },
          ].map(t => {
            const active = txnType === t.v;
            return (
              <button key={t.v || 'all'}
                      onClick={() => { setTxnType(t.v); setOffset(0); }}
                      style={{
                        padding: '5px 12px',
                        borderRadius: 999,
                        border: `1px solid ${active ? t.accent : 'var(--border-strong)'}`,
                        background: active ? t.accent : 'var(--surface)',
                        color: active ? '#fff' : 'var(--text-muted)',
                        fontWeight: active ? 700 : 500,
                        fontSize: 12.5,
                        transition: 'all .15s',
                      }}>
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <SkeletonTable rows={8} cols={6} showHeader />
      ) : (
        <>
          <div className="card pager-card">
            <div className="pager-stats">
              <div>
                <div className="label">총 건수</div>
                <div className="val">{total.toLocaleString('ko-KR')}<span className="unit">건</span></div>
              </div>
              <div>
                <div className="label">현재 페이지</div>
                <div className="val">{curPage}<span className="sep">/</span><span className="denom">{totalPages}</span></div>
              </div>
              <div>
                <div className="label">표시 항목</div>
                <div className="range">{firstIdx.toLocaleString('ko-KR')} ~ {lastIdx.toLocaleString('ko-KR')} 번째</div>
              </div>
            </div>

            <div className="pager-controls">
              <label className="per-page">한 페이지 표시
                <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setOffset(0); }}>
                  <option value={50}>50건</option>
                  <option value={100}>100건</option>
                  <option value={200}>200건</option>
                  <option value={500}>500건</option>
                </select>
              </label>

              <div className="pager-nav">
                <button onClick={() => goPage(1)} disabled={curPage <= 1} className="pager-btn icon" title="처음"><ChevronsLeft size={14} /></button>
                <button onClick={() => goPage(curPage - 1)} disabled={curPage <= 1} className="pager-btn icon" title="이전"><ChevronLeft size={14} /></button>
                <span className="pager-input">
                  <input type="number" min={1} max={totalPages} value={curPage}
                         onChange={e => goPage(Number(e.target.value))} />
                  / <span>{totalPages}</span>
                </span>
                <button onClick={() => goPage(curPage + 1)} disabled={curPage >= totalPages} className="pager-btn icon" title="다음"><ChevronRight size={14} /></button>
                <button onClick={() => goPage(totalPages)} disabled={curPage >= totalPages} className="pager-btn icon" title="마지막"><ChevronsRight size={14} /></button>
              </div>
            </div>
          </div>

          {data.length === 0 ? (
            <div className="card empty-state">
              <BellOff className="empty-icon" size={36} strokeWidth={1.6} />
              <h4>해당 조건의 결제 수신내역이 없습니다</h4>
              <p>기간이나 필터를 조정해 주세요.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div className="table-wrap" style={{ maxHeight: '65vh', border: 0 }}>
                <table>
                  <thead><tr>
                    <th className="nowrap">발생시각</th>
                    <th className="nowrap">결제 수신 유형</th>
                    <th className="nowrap" title="가맹점 고유 ID (PG가 부여) · 개발자 필드명: mcht_id">MID / 가맹점 ID</th>
                    <th>가맹점명</th>
                    <th className="nowrap">유형</th>
                    <th className="nowrap">취소</th>
                    <th className="num nowrap">매출액</th>
                    <th className="num nowrap">수수료</th>
                    <th className="num nowrap">순액</th>
                    <th className="nowrap">정산반영</th>
                    <th className="nowrap">주문번호</th>
                    <th className="nowrap">수신시각</th>
                    <th></th>
                  </tr></thead>
                  <tbody>
                    {data.map(r => {
                      const ref = reflectedLabel(r);
                      return (
                        <tr key={r.ord_num}>
                          <td className="nowrap cell-mono" style={{ color: 'var(--ink)' }}>{r.trx_at}</td>
                          <td className="nowrap">
                            <span className={`noti-kind ${r.noti_kind ? NOTI_KIND_CLASS[r.noti_kind] : ''}`}>
                              {r.noti_kind ? NOTI_KIND_KO[r.noti_kind] : '-'}
                              {r.settlmnt_cycle && <span className="cycle">{r.settlmnt_cycle}</span>}
                            </span>
                          </td>
                          <td className="nowrap cell-mono">{r.mcht_id}</td>
                          <td className="cell-name">
                            {r.mcht_name ?? '-'}
                            {r.is_test ? <span className="tag-test">테스트</span> : null}
                          </td>
                          <td className="nowrap">{TYPE_KO[r.txn_type] ?? r.txn_type}</td>
                          <td className="nowrap">{r.is_cancel ? <span className="cancel-tag">취소</span> : '-'}</td>
                          <td className={`num nowrap ${r.gross_amount < 0 ? 'cell-neg bold' : ''}`} style={{ fontWeight: 600 }}>{fmt.won(r.gross_amount)}</td>
                          <td className="num nowrap">{fmt.won(r.fee_amount)}</td>
                          <td className="num nowrap">{fmt.won(r.net_amount)}</td>
                          <td className="nowrap" style={{ color: ref.color, fontWeight: 600 }}>{ref.text}</td>
                          <td className="nowrap cell-mono">{r.ord_num}</td>
                          <td className="nowrap cell-mono">{r.created_at}</td>
                          <td>
                            <button onClick={() => setDetail(r)} className="filter-ghost" style={{ padding: '4px 10px', fontSize: 12 }}>원문</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="subtitle">노티 원문</div>
                <div className="title">{detail.ord_num}</div>
              </div>
              <button onClick={() => setDetail(null)} className="modal-close">닫기</button>
            </div>
            <div className="modal-body">
              <div className="kv-grid" style={{ marginBottom: 14 }}>
                <div className="k">결제 수신 유형</div>
                <div>
                  <span className={`noti-kind ${detail.noti_kind ? NOTI_KIND_CLASS[detail.noti_kind] : ''}`}>
                    {detail.noti_kind ? NOTI_KIND_KO[detail.noti_kind] : '-'}
                  </span>
                  {detail.settlmnt_cycle && <span style={{ marginLeft: 8, color: 'var(--muted)', fontSize: 12, fontWeight: 500 }}>· {detail.settlmnt_cycle}</span>}
                </div>
                <div className="k">MID / 가맹점 ID</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.mcht_id}</div>
                <div className="k">가맹점명</div><div>{detail.mcht_name ?? '-'}</div>
                <div className="k">발생시각</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.trx_at}</div>
                <div className="k">취소시각</div><div className="cell-mono">{detail.cxl_at ?? '-'}</div>
                <div className="k">유형</div><div>{TYPE_KO[detail.txn_type] ?? detail.txn_type}</div>
                <div className="k">매출액</div><div>{fmt.won(detail.gross_amount)}</div>
                <div className="k">수수료율</div><div>{detail.fee_rate != null ? (detail.fee_rate * 100).toFixed(2) + '%' : '-'}</div>
                <div className="k">수수료</div><div>{fmt.won(detail.fee_amount)}</div>
                <div className="k">순액</div><div>{fmt.won(detail.net_amount)}</div>
                <div className="k">승인번호</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.appr_num ?? '-'}</div>
                <div className="k">거래 ID</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.trx_id ?? '-'}</div>
                <div className="k">수신시각</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.created_at}</div>
              </div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>raw_payload</div>
              <pre className="code-block">{(() => {
                try { return JSON.stringify(JSON.parse(detail.raw_payload ?? 'null'), null, 2); }
                catch { return detail.raw_payload ?? ''; }
              })()}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
